import { useTu } from "@src/appV2/i18n/useTu";
import { Alert, Box, Stack, TextField } from "@mui/material";
import { Span, type UseModalState } from "@src/lib/ui-react";
import { useEffect, useState } from "react";
import { Button } from "../../../components/Button";
import { IconButton } from "../../../components/IconButton";
import { LoadingButton } from "../../../components/LoadingButton";
import { ResponsiveDialog } from "../../../components/ResponsiveDialog";
import { Text } from "../../../components/Text";
import { useCreateWorkerLock } from "../api/useCreateWorkerLock";
import { LOCK_EXPIRY_DAYS, MAX_REASON_LENGTH, MIN_REASON_LENGTH } from "../constants";
import { LOCK_MODAL_STEPS, type LockModalStep } from "../types";

interface LockFlowDialogProps {
  modalState: UseModalState;
  workerId: string;
  workplaceId: string;
}

export function LockFlowDialog(props: LockFlowDialogProps) {
  const { tu } = useTu("chat");

  const { modalState, workerId, workplaceId } = props;
  const [step, setStep] = useState<LockModalStep>(LOCK_MODAL_STEPS.confirmation);
  const [lockReason, setLockReason] = useState("");
  const [unlockCondition, setUnlockCondition] = useState("");
  const [error, setError] = useState<string | null>(null);
  const createWorkerLock = useCreateWorkerLock();

  const isFormValid =
    lockReason.length >= MIN_REASON_LENGTH && unlockCondition.length >= MIN_REASON_LENGTH;

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      setStep(LOCK_MODAL_STEPS.confirmation);
      setLockReason("");
      setUnlockCondition("");
      setError(null);
    }
  }, [modalState.modalIsOpen]);

  const handleSubmit = async () => {
    setError(null);
    try {
      await createWorkerLock.mutateAsync({
        workplaceId,
        workerId,
        lockReason,
        unlockCondition,
      });
      modalState.closeModal();
    } catch {
      setError(tu("Failed to lock this professional. Please try again."));
    }
  };

  const isConfirmationStep = step === LOCK_MODAL_STEPS.confirmation;

  return (
    // Full screen on mobile so the dialog respects the iOS top safe area
    // (env(safe-area-inset-top)); as a centered dialog this tall form butted up
    // against the Dynamic Island / status bar (GRO-2546). Desktop is unchanged.
    <ResponsiveDialog
      mobileVariant="fullScreen"
      modalState={modalState}
      title={isConfirmationStep ? tu("Lock Professional's Account") : undefined}
      headerContent={
        isConfirmationStep ? undefined : (
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(2),
              paddingX: theme.spacing(5),
              paddingY: theme.spacing(4),
              borderBottom: `1px solid ${theme.palette.border?.base}`,
            })}
          >
            <IconButton
              aria-label={tu("Back")}
              iconType="arrow-left"
              size="small"
              variant="outlined"
              onClick={() => {
                setStep(LOCK_MODAL_STEPS.confirmation);
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Text bold>{tu("Lock Professional's Account")}</Text>
            </Box>
            <IconButton
              aria-label={tu("Close dialog")}
              iconType="close"
              size="small"
              variant="outlined"
              onClick={modalState.closeModal}
            />
          </Box>
        )
      }
    >
      {isConfirmationStep ? (
        <>
          <Stack gap={3}>
            <Text>{tu("Locking this professional will:")}</Text>
            <Stack component="ul" gap={1} sx={{ margin: 0, paddingLeft: 4 }}>
              <li>
                <Text>{tu("Prevent them from accepting new visits")}</Text>
              </li>
              <li>
                <Text>{tu("Prevent them from being assigned to new cases")}</Text>
              </li>
            </Stack>
            <Text>
              {tu("This will last for {{days}} days. Enter lock details on the next page.", {
                values: { days: LOCK_EXPIRY_DAYS },
              })}
            </Text>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" gap={2} sx={{ marginTop: 4 }}>
            <Button variant="outlined" onClick={modalState.closeModal}>
              {tu("Cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setStep(LOCK_MODAL_STEPS.form);
              }}
            >
              {tu("Continue")}
            </Button>
          </Stack>
        </>
      ) : (
        <>
          <Stack gap={3}>
            <Stack gap={1}>
              <Text semibold>{tu("Reason for Locking")}</Text>
              <TextField
                multiline
                rows={4}
                placeholder={tu("Describe why this professional's account should be locked...")}
                value={lockReason}
                inputProps={{ maxLength: MAX_REASON_LENGTH }}
                onChange={(event) => {
                  setLockReason(event.target.value);
                }}
              />
              <Text
                variant="caption"
                sx={(theme) => ({
                  color:
                    lockReason.length < MIN_REASON_LENGTH
                      ? theme.palette.text?.secondary
                      : theme.palette.success?.main,
                  textAlign: "right",
                })}
              >
                {tu("{{currentLength}}/{{minimumLength}} minimum characters", {
                  values: {
                    currentLength: lockReason.length,
                    minimumLength: MIN_REASON_LENGTH,
                  },
                })}
              </Text>
            </Stack>

            <Stack gap={1}>
              <Text semibold>{tu("How to Unlock")}</Text>
              <TextField
                multiline
                rows={4}
                placeholder={tu(
                  "Describe what needs to happen before this professional can be unlocked..."
                )}
                value={unlockCondition}
                inputProps={{ maxLength: MAX_REASON_LENGTH }}
                onChange={(event) => {
                  setUnlockCondition(event.target.value);
                }}
              />
              <Text
                variant="caption"
                sx={(theme) => ({
                  color:
                    unlockCondition.length < MIN_REASON_LENGTH
                      ? theme.palette.text?.secondary
                      : theme.palette.success?.main,
                  textAlign: "right",
                })}
              >
                {tu("{{currentLength}}/{{minimumLength}} minimum characters", {
                  values: {
                    currentLength: unlockCondition.length,
                    minimumLength: MIN_REASON_LENGTH,
                  },
                })}
              </Text>
            </Stack>

            <Alert severity="warning" sx={{ alignItems: "center" }}>
              <Span fontWeight="bold">
                {tu("These responses will be shared with the professional in the chat.")}
              </Span>{" "}
              {tu("Please use judgment and professionalism.")}
            </Alert>

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
          <Stack direction="row" justifyContent="flex-end" gap={2} sx={{ marginTop: 4 }}>
            <Button variant="outlined" onClick={modalState.closeModal}>
              {tu("Cancel")}
            </Button>
            <LoadingButton
              isLoading={createWorkerLock.isLoading}
              variant="primary"
              disabled={!isFormValid}
              onClick={handleSubmit}
            >
              {tu("Lock Professional")}
            </LoadingButton>
          </Stack>
        </>
      )}
    </ResponsiveDialog>
  );
}
